import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from 'services/api';
import { FilterConfig } from 'hooks/useListQuery';
import { extractErrorMessage } from 'helpers/extractErrorMessage';

export interface FileEventInstance {
  file_event_instance_id: number;
  event_id: number;
  created_at: string;
  file_path: string;
  original_file_path?: string | null;
  ssh_connection_string?: string | null;
  ssh_connection_status_updates?: Array<any>;
  processed: boolean;
  created_node_instance_id: number | null;
}

interface HeaderConfig {
  header_key: string;
  header_type: string;
  label: string;
}

interface FileEventInstanceState {
  items: FileEventInstance[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  config: HeaderConfig[];
  filtersConfig: FilterConfig[];
}

const initialState: FileEventInstanceState = {
  items: [],
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  pageSize: 100,
  status: 'idle',
  error: null,
  config: [],
  filtersConfig: [],
};

export const fetchFileEventInstances = createAsyncThunk(
  'fileEventInstances/fetchFileEventInstances',
  async (
    {
      zoneName,
      eventName,
      file_event_instance_id,
      page = 1,
      limit = 100,
      created_at,
      ...filters
    }: {
      zoneName: string;
      eventName: string;
      file_event_instance_id?: string | number;
      page?: number;
      limit?: number;
      created_at?: { startDate?: string; endDate?: string };
      [key: string]: any;
    },
    thunkAPI
  ) => {
    try {
      const response = await api.get(`/file_event_instance/${zoneName}/${eventName}`, {
        params: {
          file_event_instance_id,
          page,
          limit,
          created_at_from: created_at?.startDate || undefined,
          created_at_to: created_at?.endDate || undefined,
          ...filters,
        },
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const fileEventInstancesSlice = createSlice({
  name: 'fileEventInstances',
  initialState,
  reducers: {
    setPage(state, action) {
      state.currentPage = action.payload;
    },
    setPageSize(state, action) {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFileEventInstances.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchFileEventInstances.fulfilled, (state, action) => {
        const { file_event_instances, pagination, config, filtersConfig } = action.payload;
        state.items = file_event_instances || [];
        state.config = config || [];
        state.filtersConfig = filtersConfig || [];
        state.currentPage = pagination?.current_page ?? state.currentPage;
        state.totalPages = pagination?.total_pages ?? state.totalPages;
        state.totalItems = pagination?.total_records ?? state.totalItems;
        state.pageSize = pagination?.limit ?? state.pageSize;
        state.status = 'succeeded';
      })
      .addCase(fetchFileEventInstances.rejected, (state, action) => {
        state.status = 'failed';
        state.error = extractErrorMessage(action.payload, 'Unknown error');
      });
  },
});

export const { setPage, setPageSize } = fileEventInstancesSlice.actions;
export default fileEventInstancesSlice.reducer;
